/**
 * Hand-rolled OTLP/HTTP JSON exporter.
 *
 * Why hand-rolled: the official `@opentelemetry/sdk-*` packages assume Node
 * or a full browser SDK build; both choices double the SDK's RN bundle and
 * introduce fetch/XHR polyfill quirks under Hermes. JSON-over-HTTP is part of
 * the OTLP spec (oteps-0122), Better Stack accepts it natively (multiplexed
 * by `Content-Type`), and the encoding rules are trivial: `JSON.stringify`
 * the envelope, set the header.
 *
 * Wire shape (one POST per signal):
 *   POST  https://<host>/v1/{logs,metrics,traces}
 *   Authorization: Bearer <token>
 *   Content-Type:  application/json
 *   Content-Encoding: gzip          (when CompressionStream is available)
 *
 * Batching: each signal has its own queue + timer. Flush triggers:
 *   1. Timer fires at `batchMaxMs` (default 5 s).
 *   2. Queue length reaches `batchMaxEnvelopes` (default 1000).
 *   3. Caller invokes `flush()` (pagehide / AppState.background).
 *
 * Retry policy:
 *   - 2xx                  → success.
 *   - 4xx                  → drop the batch silently (host config error).
 *   - 5xx / network error  → retry up to `retries` times with exponential
 *                            backoff (250ms, 500ms, 1000ms).
 *   - After exhaustion the batch is dropped; we never block the SDK on
 *     telemetry delivery.
 */

import type {
  OtlpExportLogsServiceRequest,
  OtlpExportMetricsServiceRequest,
  OtlpExportTraceServiceRequest,
  OtlpLogRecord,
  OtlpMetric,
  OtlpResource,
  OtlpSpan,
} from './otel-types.js';

const SCOPE_NAME = 'morfi-backend-sdk-v2';
const SCOPE_VERSION = '2.0.0';

export interface OtlpJsonExporterOpts {
  /** Better Stack ingest host, e.g. `https://s2325520.us-east-9.betterstackdata.com`. */
  readonly host: string;
  /** Bearer source token from the Better Stack source page. */
  readonly token: string;
  /** Attempt gzip compression when `CompressionStream` is available. */
  readonly gzip?: boolean;
  /** Injectable for tests. Defaults to global `fetch`. */
  readonly fetch?: typeof fetch;
  /** Injectable for tests. */
  readonly now?: () => number;
  /** Batch flush deadline, ms. Default 5000. */
  readonly batchMaxMs?: number;
  /** Batch flush size, envelopes. Default 1000. */
  readonly batchMaxEnvelopes?: number;
  /** Max retries on 5xx / network error. Default 3. */
  readonly retries?: number;
  /** Override scope version (defaults to `2.0.0`). */
  readonly scopeVersion?: string;
}

export interface OtlpJsonExporter {
  exportLog(record: OtlpLogRecord, resource: OtlpResource): void;
  exportMetric(metric: OtlpMetric, resource: OtlpResource): void;
  exportSpan(span: OtlpSpan, resource: OtlpResource): void;
  flush(): Promise<void>;
  shutdown(): Promise<void>;
}

type Signal = 'logs' | 'metrics' | 'traces';

interface Queue<T> {
  resource: OtlpResource | null;
  items: T[];
  timer: ReturnType<typeof setTimeout> | null;
}

export function createOtlpJsonExporter(opts: OtlpJsonExporterOpts): OtlpJsonExporter {
  const fetchFn = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const batchMaxMs = opts.batchMaxMs ?? 5000;
  const batchMaxEnvelopes = opts.batchMaxEnvelopes ?? 1000;
  const retries = opts.retries ?? 3;
  const scopeVersion = opts.scopeVersion ?? SCOPE_VERSION;
  const useGzip = opts.gzip === true && typeof CompressionStream !== 'undefined';
  const host = opts.host.replace(/\/$/, '');
  const authHeader = `Bearer ${opts.token}`;

  const logQ: Queue<OtlpLogRecord> = { resource: null, items: [], timer: null };
  const metricQ: Queue<OtlpMetric> = { resource: null, items: [], timer: null };
  const traceQ: Queue<OtlpSpan> = { resource: null, items: [], timer: null };

  let shutdownInProgress = false;

  function scheduleFlush(signal: Signal): void {
    const q = pick(signal);
    if (q.timer !== null) return;
    q.timer = setTimeout(() => {
      q.timer = null;
      void flushOne(signal);
    }, batchMaxMs);
  }

  function pick(signal: Signal): Queue<OtlpLogRecord> | Queue<OtlpMetric> | Queue<OtlpSpan> {
    if (signal === 'logs') return logQ;
    if (signal === 'metrics') return metricQ;
    return traceQ;
  }

  function enqueue<T extends OtlpLogRecord | OtlpMetric | OtlpSpan>(
    q: Queue<T>,
    item: T,
    resource: OtlpResource,
    signal: Signal,
  ): void {
    if (shutdownInProgress) return;
    q.resource = resource; // last-write-wins; resource is stable per process anyway
    q.items.push(item);
    if (q.items.length >= batchMaxEnvelopes) {
      if (q.timer !== null) {
        clearTimeout(q.timer);
        q.timer = null;
      }
      void flushOne(signal);
      return;
    }
    scheduleFlush(signal);
  }

  async function flushOne(signal: Signal): Promise<void> {
    const q = pick(signal);
    if (q.items.length === 0) return;
    const resource = q.resource;
    if (resource === null) {
      q.items.length = 0;
      return;
    }
    // Drain.
    const items = q.items.slice();
    q.items.length = 0;
    if (q.timer !== null) {
      clearTimeout(q.timer);
      q.timer = null;
    }

    const envelope = buildEnvelope(signal, items, resource, scopeVersion);
    const url = `${host}/v1/${signal}`;
    await postWithRetry(url, envelope, authHeader, useGzip, retries, fetchFn);
  }

  async function postWithRetry(
    url: string,
    envelope: object,
    auth: string,
    gzip: boolean,
    retriesRemaining: number,
    f: typeof fetch,
  ): Promise<void> {
    // BigInt replacer — D6/D9 cache values can be bigint; JSON.stringify
    // would otherwise throw `TypeError: Do not know how to serialize a
    // BigInt`. We coerce to string (OTLP/JSON's int64 representation
    // already uses strings, so this is wire-compatible).
    const body = JSON.stringify(envelope, (_k, v: unknown) =>
      typeof v === 'bigint' ? v.toString() : v,
    );
    let attempt = 0;
    while (true) {
      try {
        const init = await buildRequestInit(body, auth, gzip);
        const res = await f(url, init);
        if (res.ok) return;
        if (res.status >= 400 && res.status < 500) {
          // 4xx → host config issue; never retry.
          console.warn(
            `[morfi-sdk:telemetry] OTLP POST ${url} returned ${res.status}; dropping batch`,
          );
          return;
        }
        // 5xx — fall through to retry.
      } catch {
        // Network error — fall through to retry.
      }
      if (attempt >= retriesRemaining) return;
      const delay = 250 * 2 ** attempt;
      await sleep(delay);
      attempt += 1;
    }
  }

  return {
    exportLog(record: OtlpLogRecord, resource: OtlpResource): void {
      enqueue(logQ, record, resource, 'logs');
    },
    exportMetric(metric: OtlpMetric, resource: OtlpResource): void {
      enqueue(metricQ, metric, resource, 'metrics');
    },
    exportSpan(span: OtlpSpan, resource: OtlpResource): void {
      enqueue(traceQ, span, resource, 'traces');
    },
    async flush(): Promise<void> {
      await Promise.all([flushOne('logs'), flushOne('metrics'), flushOne('traces')]);
    },
    async shutdown(): Promise<void> {
      shutdownInProgress = true;
      await Promise.all([flushOne('logs'), flushOne('metrics'), flushOne('traces')]);
    },
  };
}

function buildEnvelope(
  signal: Signal,
  items: ReadonlyArray<OtlpLogRecord | OtlpMetric | OtlpSpan>,
  resource: OtlpResource,
  scopeVersion: string,
): OtlpExportLogsServiceRequest | OtlpExportMetricsServiceRequest | OtlpExportTraceServiceRequest {
  const scope = { name: SCOPE_NAME, version: scopeVersion };
  if (signal === 'logs') {
    return {
      resourceLogs: [
        {
          resource,
          scopeLogs: [{ scope, logRecords: items as ReadonlyArray<OtlpLogRecord> }],
        },
      ],
    };
  }
  if (signal === 'metrics') {
    return {
      resourceMetrics: [
        {
          resource,
          scopeMetrics: [{ scope, metrics: items as ReadonlyArray<OtlpMetric> }],
        },
      ],
    };
  }
  return {
    resourceSpans: [
      {
        resource,
        scopeSpans: [{ scope, spans: items as ReadonlyArray<OtlpSpan> }],
      },
    ],
  };
}

async function buildRequestInit(body: string, auth: string, gzip: boolean): Promise<RequestInit> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: auth,
  };
  let payload: BodyInit = body;
  if (gzip) {
    // CompressionStream is available in all modern browsers + RN 0.74+
    // with the polyfill, but we still feature-detect.
    const compressed = await gzipString(body);
    if (compressed !== null) {
      // Copy into a fresh ArrayBuffer so the static type narrows to
      // BlobPart cleanly (Uint8Array<ArrayBufferLike> doesn't satisfy
      // ArrayBufferView<ArrayBuffer> after the lib es2024 change).
      const buf = new ArrayBuffer(compressed.byteLength);
      new Uint8Array(buf).set(compressed);
      payload = new Blob([buf]);
      headers['Content-Encoding'] = 'gzip';
    }
  }
  // `keepalive` lets unload-time flushes complete after the page is gone,
  // but browsers cap keepalive bodies at 64 KB and silently drop oversize
  // posts. For SDK telemetry batches we cap at 32 KB (after compression
  // when present) — anything larger ships without keepalive and may not
  // survive unload; the next page load will retry from the SDK's bus
  // anyway, so a single dropped batch is recoverable.
  const KEEPALIVE_CAP_BYTES = 32 * 1024;
  const payloadSize =
    typeof payload === 'string'
      ? payload.length
      : payload instanceof Blob
        ? payload.size
        : Number.POSITIVE_INFINITY;
  const init: RequestInit = {
    method: 'POST',
    headers,
    body: payload,
  };
  if (payloadSize <= KEEPALIVE_CAP_BYTES) {
    init.keepalive = true;
  }
  return init;
}

async function gzipString(input: string): Promise<Uint8Array | null> {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const cs = new CompressionStream('gzip');
    const stream = new Blob([input]).stream().pipeThrough(cs);
    const buf = await new Response(stream).arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
